import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { catchError, concatMap, shareReplay } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  DashboardResponse,
  LoginOAuthParams,
  OauthAEMConfig,
  RedirectfulResponse
} from '../../../../models/login';
import { UserSessionHelperService } from './user-session-helper.service';

@Injectable({
  providedIn: 'root'
})
export class UserSessionService extends UserSessionHelperService {
  getUserSession(sessionTimeOut = false): Observable<DashboardResponse> {
    if (!this.loginRememberMeUtils.checkLoginUrls()) {
      if (this.data$ && !sessionTimeOut) {
        return this.data$;
      } else {
        this.pkceCode = this.loginRememberMeUtils.generateCodeChallenge();
        return this.userData.getAuthConfigContent().pipe(
          concatMap((authConfig: OauthAEMConfig) => {
            const loginOAuthParams: LoginOAuthParams = {
              oauthConfig: authConfig,
              pkceCode: this.pkceCode
            };
            return this.getUserSessionHelper(sessionTimeOut, loginOAuthParams);
          }),
          catchError((error) => {
            return this.nonLoggedInStateHelper(error);
          })
        );
      }
    } else {
      return this.nonLoggedInStateHelper();
    }
  }

  getUserSessionHelper(
    sessionTimeOut: boolean,
    loginOAuthParams: LoginOAuthParams
  ): Observable<DashboardResponse> {
    return from(this.isTokenAvailable()).pipe(
      concatMap((tokenAvailable: boolean) => {
        return from(this.setTokenDetails()).pipe(
          concatMap(() => {
            if (
              this.checkServiceWorkerSession(
                tokenAvailable,
                sessionTimeOut,
                loginOAuthParams.oauthConfig?.REFRESH_TOKEN_TIME
                  ? loginOAuthParams.oauthConfig.REFRESH_TOKEN_TIME
                  : 300000
              )
            ) {
              this.deleteStatusValuesFromServiceWorker();
              this.data$ = this.userData
                .getDashBrdData(loginOAuthParams.oauthConfig, '')
                .pipe(shareReplay(1));
              return this.data$;
            } else {
              return this.getUserSessionData(loginOAuthParams);
            }
          }),
          catchError((error) => {
            return this.nonLoggedInStateHelper(error);
          })
        );
      }),
      catchError((error) => {
        return this.nonLoggedInStateHelper(error);
      })
    );
  }

  getUserSessionData(
    loginOAuthParams: LoginOAuthParams
  ): Observable<DashboardResponse> {
    return from(this.setStatusValueFromServiceWorker()).pipe(
      concatMap((response: boolean) => {
        if (response) {
          return this.nonLoggedInStateHelper();
        } else {
          if (!loginOAuthParams.oauthConfig.ENABLE_NEW_PERSISTENT_LOGIN) {
            this.data$ = this.userData.getDashBrdData(
              loginOAuthParams.oauthConfig,
              ''
            );
            return this.data$;
          } else {
            return this.getTokenAndUserSessionData(loginOAuthParams);
          }
        }
      }),
      catchError((error) => {
        return this.nonLoggedInStateHelper(error);
      })
    );
  }

  getTokenAndUserSessionData(
    loginOAuthParams: LoginOAuthParams
  ): Observable<DashboardResponse> {
    return this.loginRememberMeWrapper
      .initiatePingFlow(loginOAuthParams, 'INITIATE_LOGIN')
      .pipe(
        concatMap(
          (
            response:
              | AuthAccessTokenDetails
              | RedirectfulResponse
              | boolean
              | string
          ) => {
            if (typeof response === 'string' || typeof response === 'boolean') {
              return this.nonLoggedInStateHelper();
            } else if ('access_token' in response) {
              this.loginRememberMeUtils.sendTokenServiceWorker(
                loginOAuthParams.oauthConfig,
                response
              );
              this.tokenValue$.next(true);
              this.data$ = this.userData
                .getDashBrdData(
                  loginOAuthParams.oauthConfig,
                  response.access_token
                )
                .pipe(shareReplay(1));
              return this.data$;
            } else {
              return this.nonLoggedInStateHelper();
            }
          }
        ),
        catchError((error) => {
          return this.nonLoggedInStateHelper(error);
        })
      );
  }
}
